import { useApolloClient } from '@apollo/client';
import { FlashbarProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { remove } from 'aws-amplify/storage';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
  GetWebsiteEventsDocument,
  GetWebsiteEventsQuery,
  useUpdateWebsiteEventsMutation,
  WebsiteEvents,
} from '../../../../generated/graphql';
import { FormValues, schema } from './Create';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

type EditProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  selectedId?: string | null;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
};

export default function Edit({
  visible,
  selectedId,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
}: EditProps) {
  const [imageKey, setImageKey] = useState<string | undefined>();
  const [keyToDelete, setKeyToDelete] = useState<string | undefined>();
  const client = useApolloClient();
  const [updateWebsiteEvents, { loading: updateLoading }] =
    useUpdateWebsiteEventsMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (selectedId && selectedId.length > 0) {
      const items = client.readQuery({
        query: GetWebsiteEventsDocument,
      }) as GetWebsiteEventsQuery;
      if (
        items &&
        items.listWebsiteEvents &&
        items.listWebsiteEvents.items &&
        items.listWebsiteEvents.items.length > 0
      ) {
        const item = items.listWebsiteEvents.items.filter(
          (item) => item?.id === selectedId,
        )[0];
        if (item) {
          if (item.imageurl) {
            setImageKey(item.imageurl);
            setKeyToDelete(item.imageurl);
          }
          methods.reset({
            title: item.title ?? '',
            location: item.location ?? '',
            range: {
              startDate: DateTime.fromSeconds(item.from ?? 0).toFormat(
                'y-LL-dd',
              ),
              endDate: DateTime.fromSeconds(item.to ?? 0).toFormat('y-LL-dd'),
              type: 'absolute',
            },
            is_active: item.is_active ?? true,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, methods.reset, selectedId]);

  const onSubmit = (data: any) => {
    if (selectedId && selectedId.length > 0) {
      const entry = createDDBItem(data);
      if (imageKey && !isEmpty(imageKey)) {
        entry['imageurl'] = imageKey;
      } else {
        entry['imageurl'] = null;
      }
      const messageId = uuidv4();
      notifyInProgress({ id: messageId, header: 'Eintrag wird bearbeitet...' });
      updateWebsiteEvents({
        variables: { input: { id: selectedId, ...entry } },
        update: (cache, { data }) => {
          const addData = data?.updateWebsiteEvents;
          const existingData = cache.readQuery({
            query: GetWebsiteEventsDocument,
          }) as GetWebsiteEventsQuery;
          const oldData = existingData.listWebsiteEvents
            ?.items as WebsiteEvents[];
          cache.writeQuery({
            query: GetWebsiteEventsDocument,
            data: {
              listWebsiteEvents: {
                items: oldData.map((item) => {
                  if (item.id === selectedId) {
                    return { ...addData };
                  } else {
                    return {
                      ...item,
                    };
                  }
                }),
              },
            },
          });
        },
      })
        .then((data) => {
          const result = data.data?.updateWebsiteEvents;
          if (result) {
            if (keyToDelete) {
              remove({
                path: keyToDelete,
              });
            }
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              content: `Eintrag mit dem Namen "${result.title}" wurde bearbeitet.`,
              header: 'Gespeichert',
              dismissible: true,
            });
            setVisible(false);
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  const handleImageUrlChange = useCallback((key?: string) => {
    setImageKey(key);
  }, []);

  const disableSubmit = useMemo(() => {
    if (imageKey || !isEmpty(methods.formState.dirtyFields)) return false;
    if (isEmpty(imageKey)) return false;
    if (!imageKey) return true;
    if (isEmpty(methods.formState.dirtyFields)) return true;
    if (updateLoading) return true;
    return false;
  }, [imageKey, methods, updateLoading]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={disableSubmit}
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag bearbeiten'}
      >
        <Form changeImageUrl={handleImageUrlChange} imageKey={imageKey} />
      </Modal>
    </FormProvider>
  );
}
